import { useCallback, useEffect, useMemo, useState } from "react";
import withRouter from "Components/Common/withRouter";
import OrganizationChartTemplate from "Components/templates/hr/organization-chart"
import { createSelector } from "reselect";
import { HumanResourcesSliceState, setShowSyncModal } from "slices/human-resources/reducer";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { biuldOrgChartTree, getCharts, getOrgChart, reduceChildMap, reduceNodes, syncDayforceData } from "slices/human-resources/thunk";
import { D3OrgChartNode, Department } from "models/org-chart-node";
import useManual from "hooks/useManual"
import useSyncProcess from "hooks/useSyncProcess"
import { SyncLogTypes } from "models/sync-log"

const selectHumanResources = createSelector(
  (state: any) => state.HumanResources,
  ({ orgChartNodes, charts, showSyncModal, isSyncingData }: HumanResourcesSliceState) => ({
    orgChartNodes, charts, showSyncModal, isSyncingData
  })
)

const DEFAULT_ORG_CHART = 'Organization Chart'

const GeneralOrgChartPage = () => {
  document.title = "Organizaiton Chart | IbexIQ";
  const { handleOpenManual } = useManual("human-resources/org-chart");
  const dispatch: any = useDispatch();
  const { orgChartNodes, charts, showSyncModal } = useSelector(selectHumanResources);
  const [tree, setTree] = useState<Map<string, D3OrgChartNode[]>>(new Map());
  const [chart, setChart] = useState<Department | null>(null);

  const handleFocusChart = (chart: Department) => setChart(chart);

  const data = useMemo(() => reduceNodes(tree, orgChartNodes, chart), [tree, orgChartNodes, chart, reduceNodes])
  const children = useMemo(() => reduceChildMap(tree), [tree, reduceChildMap])

  useEffect(() => {
    if (orgChartNodes.length === 0) {
      dispatch(getOrgChart())
    }

    if (charts.length === 0) {
      dispatch(getCharts())
    }
  }, [])

  // Default Chart
  useEffect(() => {
    const chart = charts.find(c => c.description === DEFAULT_ORG_CHART)

    if (chart) setChart(chart)
  }, [charts, setChart])

  // Organize chart into tree
  useEffect(() => {
    setTree(biuldOrgChartTree(orgChartNodes))
  }, [orgChartNodes, setTree, biuldOrgChartTree])

  // This just opens the are you sure modal.
  const handleOpenAreYouSureModal = () => {
    dispatch(setShowSyncModal({ show: true }))
  }

  const handleAreYouSureDecline = async () => {
    dispatch(setShowSyncModal({ show: false }))
  }

  const onModalClose = () => {
    dispatch(setShowSyncModal({ show: false }))
  }


  const { isSyncing: isSyncingData, startSync } = useSyncProcess({
    module: SyncLogTypes.EMPLOYEE_DAYFORCE,
    start: syncDayforceData,
    onFinished: () => { dispatch(getOrgChart()) },
  })

  const handleSyncData = async () => {
    dispatch(setShowSyncModal({ show: false }))
    await startSync()
  }

  return <OrganizationChartTemplate
    fullScreen={false}
    childrenMap={children}
    data={data.map(n => JSON.parse(JSON.stringify(n)))}
    charts={charts}
    focusedChart={chart}
    setFocusedChart={handleFocusChart}
    shiftHeight={94}
    isOpen={showSyncModal}
    handleSyncData={handleOpenAreYouSureModal}
    handleAreYouSureDecline={handleAreYouSureDecline}
    handleAreYouSureAccept={handleSyncData}
    areYouSureacceptText={isSyncingData ? 'Syncing...' : "Sync Data"}
    areYouSuredeclineText={"Cancel"}
    onModalClose={onModalClose}
    modalTitle={"Are you sure you want to sync?"}
    handleOpenHelpDocument={handleOpenManual}

  />
}

export default withRouter(GeneralOrgChartPage)


